import type { PermissionScope } from '@/lib/types';

/*
 * The permission grid's WRITE model — what a tick and an untick mean.
 *
 * Pure, and deliberately in its own module rather than inside
 * `PermissionGrid.tsx`: every one of these decisions is a claim about what gets
 * PUT to `/permission-sets/:id/items`, and the grid, its bulk toggles, the
 * counts it prints and the save confirmation on `PermissionSetDetailPage` all
 * have to agree about them. Two copies of "is this cell ticked?" would agree on
 * the day they were written and drift at the first edge.
 *
 * ── The rule the owner set (2026-08-30) ───────────────────────────────────
 *
 *   ticking   → a GRANT. `all`, unless the cell already holds a grant, in
 *               which case its scope is left alone (`own` must survive a bulk
 *               tick — see `nextScopeForToggle`).
 *   unticking → `none`, ALWAYS. An explicit, stored denial, for every module.
 *
 * Since the strict model (backend ef55429, 2026-10-01) the server reads an
 * absent grant as a DENIAL too, so `none` and absent now behave alike there;
 * the explicit row is kept because the seeded sets carry one row per catalogue
 * permission (owner decision 2026-10-02) and a save is a full replace.
 *
 * ── Scope (owner decision 2026-10-02) ─────────────────────────────────────
 *
 * Scope is chosen PER MODULE, and only on the modules where the server really
 * filters by owner (`OWN_ENFORCED_MODULES` in `@/lib/permission-sections`).
 * Everywhere else a grant is written at `all`. `moduleScopeOf` reads the
 * module's current scope off its granted rows and `applyModuleScope` rewrites
 * every granted row of the module at once.
 *
 * A person resolves to exactly one set, through their role, so an explicit
 * `none` here is the whole answer for that set's holders.
 */

/** Absent from the set — the third state, and NOT a scope. */
export type CellState = PermissionScope | undefined;

/** Permission id → its scope in this set. Absent key = not in the set. */
export type ScopeMap = ReadonlyMap<number, PermissionScope>;

/** What a tick writes when there is no existing grant to preserve. */
export const GRANT_SCOPE = 'all' satisfies PermissionScope;

/** What an untick writes. The whole point of this module. */
export const DENY_SCOPE = 'none' satisfies PermissionScope;

/**
 * Does this cell let the person act?
 *
 * A type predicate, so `nextScopeForToggle` can hand the existing scope back
 * without widening it. ⚠️ **Never `map.has(id)`** — that was the membership
 * test, and now that unticking stores a row, a denied cell IS in the map. Every
 * "is this ticked" question goes through here.
 */
export function isGranting(scope: CellState): scope is 'all' | 'own' {
  return scope === 'all' || scope === 'own';
}

/** Explicitly denied — in the set, and saying no. */
export function isDenied(scope: CellState): boolean {
  return scope === DENY_SCOPE;
}

/**
 * The one decision, for one cell.
 *
 * `on` is the checkbox's new state, so this answers "what does the set store
 * now". It always returns a scope: there is no branch that removes a row.
 *
 * `grantScope` is what a tick writes. Omitted, a tick preserves an existing
 * grant's scope (so a bulk tick never silently widens `own`) and otherwise
 * writes `all`. The grid passes `'all'` on modules with no scope picker, and the
 * module's own scope on modules that have one.
 */
export function nextScopeForToggle(
  current: CellState,
  on: boolean,
  grantScope?: 'all' | 'own',
): PermissionScope {
  if (!on) return DENY_SCOPE;
  if (grantScope) return grantScope;
  return isGranting(current) ? current : GRANT_SCOPE;
}

/**
 * Apply a tick or untick across any number of permissions, returning a NEW map.
 *
 * Used by the single toggle (one id), the module switch and the section-level
 * grant-all / clear-all (many), so they cannot disagree. Unticking writes
 * `none` even for a cell that was ABSENT, so the full-replace PUT always says
 * no explicitly.
 */
export function applyToggle(
  value: ScopeMap,
  permissionIds: Iterable<number>,
  on: boolean,
  grantScope?: 'all' | 'own',
): Map<number, PermissionScope> {
  const next = new Map(value);
  for (const permissionId of permissionIds) {
    next.set(permissionId, nextScopeForToggle(next.get(permissionId), on, grantScope));
  }
  return next;
}

/**
 * One module's scope, read off its GRANTED rows. `mixed` only arises from data
 * written before the per-module picker (one action `own`, another `all`); the
 * picker then shows no segment selected, and choosing one evens them out.
 * A module with nothing granted reads as `all`.
 */
export function moduleScopeOf(
  value: ScopeMap,
  permissionIds: readonly number[],
): 'all' | 'own' | 'mixed' {
  let all = false;
  let own = false;
  for (const id of permissionIds) {
    const scope = value.get(id);
    if (scope === 'all') all = true;
    else if (scope === 'own') own = true;
  }
  if (all && own) return 'mixed';
  return own ? 'own' : 'all';
}

/**
 * Set the scope on every GRANTED row of a module. Denied and absent rows are
 * left alone: the picker narrows what is granted, it never grants.
 */
export function applyModuleScope(
  value: ScopeMap,
  permissionIds: Iterable<number>,
  scope: 'all' | 'own',
): Map<number, PermissionScope> {
  const next = new Map(value);
  for (const id of permissionIds) {
    if (isGranting(next.get(id))) next.set(id, scope);
  }
  return next;
}

/** How many of these permissions the set grants — the section header count. */
export function grantedCount(value: ScopeMap, permissionIds: readonly number[]): number {
  return permissionIds.filter((id) => isGranting(value.get(id))).length;
}

/**
 * The state of a bulk checkbox over a group of permissions.
 *
 * Counts GRANTS, never membership: a module every one of whose actions is
 * explicitly denied is fully unticked, and its row checkbox must read as off
 * rather than as on-because-there-are-rows.
 */
export function tallyOf(
  value: ScopeMap,
  permissionIds: readonly number[],
): { all: boolean; some: boolean } {
  const granted = permissionIds.filter((id) => isGranting(value.get(id))).length;
  return {
    all: granted > 0 && granted === permissionIds.length,
    some: granted > 0 && granted < permissionIds.length,
  };
}

/** Row counts for the grid's own summary line and for the save confirmation. */
export function summarise(value: ScopeMap): { rows: number; granting: number; denied: number } {
  let granting = 0;
  let denied = 0;
  for (const scope of value.values()) {
    if (isGranting(scope)) granting += 1;
    else if (isDenied(scope)) denied += 1;
  }
  return { rows: value.size, granting, denied };
}

/**
 * The gesture worth confirming: the draft grants NOTHING, and the set being
 * edited granted something.
 *
 * ⚠️ This replaces a `draft.size === 0` test, which this change made
 * **unreachable** — nothing removes a row any more, so a draft can never shrink
 * to empty from a set that had rows. Left as it was, the confirmation would
 * have quietly stopped firing on the one gesture it existed for: unticking
 * everything. The gesture is unchanged; what it now writes is a denial per
 * permission rather than an empty list.
 */
export function revokesEverything(next: ScopeMap, previous: ScopeMap): boolean {
  return summarise(next).granting === 0 && summarise(previous).granting > 0;
}

/**
 * The `PUT /permission-sets/:id/items` body, in full-replace form.
 *
 * Built by walking the map, so a `permissionId` cannot repeat — the server
 * answers 422 naming the duplicate, and a Map cannot produce one.
 */
export function toItemsPayload(
  value: ScopeMap,
): { permissionId: number; scope: PermissionScope }[] {
  return Array.from(value, ([permissionId, scope]) => ({ permissionId, scope }));
}
